/**
 * Forgot Password Page
 * Allows users to request a password reset email
 */
import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Mail, Loader2, CheckCircle, Lock } from "lucide-react";

export default function ForgotPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");

  // If token is present, show the reset form
  if (token) {
    return <ResetPasswordForm token={token} />;
  }

  // Otherwise show the request form
  return <RequestResetForm />;
}

function RequestResetForm() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await fetch(`${API}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      if (res.ok) {
        setSubmitted(true);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Something went wrong. Please try again.");
      }
    } catch {
      toast.error("Network error. Please check your connection.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex">
        <div className="w-full lg:w-1/2 flex flex-col justify-center px-6 md:px-12 lg:px-24 py-12">
          <Link to="/login" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-12" data-testid="back-to-login">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm uppercase tracking-wider">Back to Login</span>
          </Link>

          <div className="max-w-md">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-500" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
                Check Your Email
              </h1>
            </div>
            <p className="text-white/50 text-lg mb-8">
              If an account exists for <strong className="text-white">{email}</strong>, you'll receive a password reset link shortly.
            </p>
            <div className="bg-white/[0.03] border border-white/10 rounded-lg p-6 mb-8">
              <p className="text-white/60 text-sm leading-relaxed">
                <strong className="text-white">Didn't receive the email?</strong><br />
                Check your spam folder, or make sure you entered the correct email address.
              </p>
            </div>
            <Button
              onClick={() => setSubmitted(false)}
              variant="outline"
              className="w-full border-white/20 text-white hover:bg-white/10 rounded-sm font-medium py-3"
              data-testid="try-again-btn"
            >
              Try a Different Email
            </Button>
          </div>
        </div>

        <div className="hidden lg:block lg:w-1/2 relative">
          <img 
            src="https://images.unsplash.com/photo-1493599398321-988db97c9591?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxjaW5lbWElMjBjYW1lcmElMjBsZW5zJTIwZ2VhciUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzcwMTk1NDM5fDA&ixlib=rb-4.1.0&q=85"
            alt="Camera gear"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] to-transparent" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex">
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-6 md:px-12 lg:px-24 py-12">
        <Link to="/login" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-12" data-testid="back-to-login">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm uppercase tracking-wider">Back to Login</span>
        </Link>

        <div className="max-w-md">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-3">
            Forgot Password?
          </h1>
          <p className="text-white/50 text-lg mb-10">
            Enter your email and we'll send you a link to reset your password.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm text-white/60 uppercase tracking-wider">
                Email Address
              </Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="bg-white/[0.05] border-white/10 focus:border-white/40 text-white pl-12 h-12 rounded-sm"
                  required
                  data-testid="forgot-email-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-white text-black hover:bg-gray-200 rounded-sm font-bold py-3 uppercase tracking-wide"
              data-testid="forgot-submit-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Send Reset Link"}
            </Button>
          </form>

          <p className="text-center text-white/50 mt-8">
            Remember your password?{" "}
            <Link to="/login" className="text-white hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      <div className="hidden lg:block lg:w-1/2 relative">
        <img 
          src="https://images.unsplash.com/photo-1493599398321-988db97c9591?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxjaW5lbWElMjBjYW1lcmElMjBsZW5zJTIwZ2VhciUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzcwMTk1NDM5fDA&ixlib=rb-4.1.0&q=85"
          alt="Camera gear"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] to-transparent" />
      </div>
    </div>
  );
}

function ResetPasswordForm({ token }) {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (!/[A-Z]/.test(newPassword)) {
      toast.error("Password must contain at least one uppercase letter");
      return;
    }
    if (!/[0-9]/.test(newPassword)) {
      toast.error("Password must contain at least one number");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    setLoading(true);

    // Use XMLHttpRequest to avoid Emergent platform fetch interceptor
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${API}/auth/reset-password`, true);
    xhr.setRequestHeader("Content-Type", "application/json");
    
    xhr.onload = function() {
      setLoading(false);
      let data = {};
      try {
        data = JSON.parse(xhr.responseText);
      } catch (e) {
        // JSON parsing failed
      }
      
      if (xhr.status >= 200 && xhr.status < 300) {
        setSuccess(true);
        toast.success("Password reset successfully!");
      } else {
        toast.error(data.detail || "Failed to reset password");
      }
    };
    
    xhr.onerror = function() {
      setLoading(false);
      toast.error("Network error. Please try again.");
    };
    
    xhr.send(JSON.stringify({ token, new_password: newPassword }));
  };

  if (success) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] text-white flex">
        <div className="w-full lg:w-1/2 flex flex-col justify-center px-6 md:px-12 lg:px-24 py-12">
          <div className="max-w-md">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-500" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
                Password Reset!
              </h1>
            </div>
            <p className="text-white/50 text-lg mb-8">
              Your password has been changed successfully. You can now log in with your new password.
            </p>
            <Link to="/login">
              <Button
                className="w-full bg-white text-black hover:bg-gray-200 rounded-sm font-bold py-3 uppercase tracking-wide"
                data-testid="go-to-login-btn"
              >
                Go to Login
              </Button>
            </Link>
          </div>
        </div>

        <div className="hidden lg:block lg:w-1/2 relative">
          <img 
            src="https://images.unsplash.com/photo-1493599398321-988db97c9591?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxjaW5lbWElMjBjYW1lcmElMjBsZW5zJTIwZ2VhciUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzcwMTk1NDM5fDA&ixlib=rb-4.1.0&q=85"
            alt="Camera gear"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] to-transparent" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex">
      <div className="w-full lg:w-1/2 flex flex-col justify-center px-6 md:px-12 lg:px-24 py-12">
        <Link to="/login" className="flex items-center gap-2 text-white/60 hover:text-white transition-colors mb-12" data-testid="back-to-login">
          <ArrowLeft className="w-4 h-4" />
          <span className="text-sm uppercase tracking-wider">Back to Login</span>
        </Link>

        <div className="max-w-md">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-3">
            Create New Password
          </h1>
          <p className="text-white/50 text-lg mb-10">
            Enter your new password below.
          </p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="newPassword" className="text-sm text-white/60 uppercase tracking-wider">
                New Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <Input
                  id="newPassword"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-white/[0.05] border-white/10 focus:border-white/40 text-white pl-12 h-12 rounded-sm"
                  required
                  minLength={6}
                  data-testid="new-password-input"
                />
              </div>
              <p className="text-white/40 text-xs">Min 8 characters, 1 uppercase, 1 number</p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-sm text-white/60 uppercase tracking-wider">
                Confirm Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/30" />
                <Input
                  id="confirmPassword"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  className="bg-white/[0.05] border-white/10 focus:border-white/40 text-white pl-12 h-12 rounded-sm"
                  required
                  data-testid="confirm-password-input"
                />
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-white text-black hover:bg-gray-200 rounded-sm font-bold py-3 uppercase tracking-wide"
              data-testid="reset-submit-btn"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : "Reset Password"}
            </Button>
          </form>
        </div>
      </div>

      <div className="hidden lg:block lg:w-1/2 relative">
        <img 
          src="https://images.unsplash.com/photo-1493599398321-988db97c9591?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzOTB8MHwxfHNlYXJjaHwyfHxjaW5lbWElMjBjYW1lcmElMjBsZW5zJTIwZ2VhciUyMGNsb3NlJTIwdXB8ZW58MHx8fHwxNzcwMTk1NDM5fDA&ixlib=rb-4.1.0&q=85"
          alt="Camera gear"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] to-transparent" />
      </div>
    </div>
  );
}
